import { Translator } from 'Translator';
import useExperiment from 'hooks/useExperiment';
import { Account } from 'model/IChat';
import { ExperienceSource } from 'model/IExperience';
import React from 'react';
import CurrencyHelper from 'utils/CurrencyHelper';
import { experienceDuration } from 'utils/ExperienceFormattingHelper';
import {
  translateCuisine,
  translateSubcategoryOrTag,
} from 'utils/TranslationHelper';
import getPrettyPrice from 'utils/getPrettyPrice';
import { useCurrencyContext } from '~/context/CurrencyContext';

const CSS = require('./TopSearchCardDetails.less');

interface TopSearchCardDetailsProps {
  experience: ExperienceSource;
  nrOfAdults: number;
  nrOfChildren: number;
  isMobile: boolean;
  useAdvertisedPrice?: boolean;
  hosts: Account[];
}

function Price({ experience, nrOfAdults, nrOfChildren, useAdvertisedPrice }) {
  const { state } = useCurrencyContext();
  const roundingExperiment = useExperiment('roundedPriceCards');
  const roundPrice = roundingExperiment.assignedVariant === 1;
  let price;
  if (useAdvertisedPrice) {
    const advertisedPrice =
      (experience?.pricing?.advertised_price_origin?.amount?.centValue ?? 0) /
      100;
    price = CurrencyHelper.getPrettyPriceFromOtherCurrency(
      advertisedPrice,
      'EUR',
      state.currency,
      roundPrice,
    );
  } else {
    price = getPrettyPrice(
      experience,
      state.currency,
      nrOfAdults,
      nrOfChildren,
      roundPrice,
    );
  }
  return <span>{Translator.translate('Common.Pricing.pp', { price })}</span>;
}

const renderHostInfo = (experience, hosts) => {
  if (hosts.length > 1) {
    return Translator.translate(
      'SearchPage.topSearches.hostInformationTextMany',
      {
        nrOfHosts: <span className={CSS.NrOfHosts}>{hosts.length}</span>,
        firstName: hosts[0].firstName,
      },
      false,
      'Connect with {firstName} or one of {nrOfHosts} other local hosts',
    );
  }
  const account = hosts?.[0] ?? experience.account;
  return Translator.translate(
    'SearchPage.topSearches.hostInformationTextSingle',
    { firstName: account.firstName },
    false,
    'with {firstName}',
  );
};

const getSubCategory = (experience) => {
  const subCategory = experience?.attributes?.subCategory;

  if (subCategory === 'Home dinners' || subCategory === 'Cooking classes') {
    const [cuisine] = experience?.cuisines ?? [''];
    const cuisineTranslated = translateCuisine(cuisine);
    return (
      <span>
        {cuisineTranslated} {translateSubcategoryOrTag(subCategory)}
      </span>
    );
  }

  return translateSubcategoryOrTag(subCategory, true);
};

const renderExperienceInfo = (
  experience,
  nrOfAdults,
  nrOfChildren,
  useAdvertisedPrice,
) => {
  return (
    <div>
      <Price
        experience={experience}
        nrOfAdults={nrOfAdults}
        nrOfChildren={nrOfChildren}
        useAdvertisedPrice={useAdvertisedPrice}
      />
      <span className={CSS.Bullet}>&#8226;</span>
      <span>{experienceDuration(experience)}</span>
      <span className={CSS.Bullet}>&#8226;</span>
      <span>{getSubCategory(experience)}</span>
    </div>
  );
};

const TopSearchCardDetails = ({
  experience,
  hosts,
  nrOfAdults,
  nrOfChildren,
  useAdvertisedPrice,
  isMobile,
}: TopSearchCardDetailsProps) => {
  return (
    <div className={CSS.TopSearchCardDetails}>
      {isMobile ? (
        <span>
          {Translator.translate(
            'OriginalPopover.host.label',
            {},
            true,
            'Choose your favorite local host',
          )}
        </span>
      ) : (
        <div>
          {renderHostInfo(experience, hosts)}
          {renderExperienceInfo(
            experience,
            nrOfAdults,
            nrOfChildren,
            useAdvertisedPrice,
          )}
        </div>
      )}
    </div>
  );
};

export default TopSearchCardDetails;
